import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import NotificationDropdown from "@/components/NotificationDropdown";
import { Radar, LayoutDashboard, PlusCircle, User, Briefcase, LogIn, LogOut, UserCircle, Globe, Home, Mail } from "lucide-react";

export default function Navbar() {
  const { user, logout, updateRole } = useAuth();
  const { lang, setLang, t, languages } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const role = user?.role || "client";
  const [showLangMenu, setShowLangMenu] = useState(false);

  const isActive = (path) => location.pathname === path;

  const linkClass = (path) =>
    `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-300 ${
      isActive(path)
        ? "text-[#00F0FF] border-b-2 border-[#00F0FF]"
        : "text-[#A0A0B0] hover:text-white border-b-2 border-transparent"
    }`;

  const handleToggleRole = async () => {
    const newRole = role === "client" ? "freelancer" : "client";
    try {
      await updateRole(newRole);
    } catch (err) {
      console.error("Role update failed:", err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 glass-surface border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="w-9 h-9 flex items-center justify-center neon-border rounded-lg">
              <Radar className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <span className="font-['Outfit'] text-lg font-semibold tracking-tight text-white">
              Project Radar <span className="neon-text">AR</span>
            </span>
          </Link>

          {/* Nav Links */}
          <div className="flex items-center gap-1">
            <Link to="/" className={linkClass("/")} data-testid="nav-home">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">{t.nav.home}</span>
            </Link>
            <Link to="/dashboard" className={linkClass("/dashboard")} data-testid="nav-dashboard">
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">{user ? t.nav.dashboard : (t.nav.browse || "Projekte ansehen")}</span>
            </Link>
            {user && role === "client" && (
              <Link to="/new-project" className={linkClass("/new-project")} data-testid="nav-new-project">
                <PlusCircle className="w-4 h-4" />
                <span className="hidden sm:inline">{t.nav.newProject}</span>
              </Link>
            )}
            <Link to="/contact" className={linkClass("/contact")} data-testid="nav-contact">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">{t.nav.contact}</span>
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <div className="relative">
              <button data-testid="lang-toggle" onClick={() => setShowLangMenu(p => !p)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-xs text-[#A0A0B0]">
                <Globe className="w-3.5 h-3.5" />
                <span className="font-['JetBrains_Mono'] uppercase">{lang}</span>
              </button>
              {showLangMenu && (
                <div className="absolute top-full right-0 mt-1 glass-surface border border-white/10 rounded-lg overflow-hidden z-50" style={{ minWidth: "120px" }}>
                  {languages.map(l => (
                    <button key={l.code} data-testid={`lang-${l.code}`}
                      onClick={() => { setLang(l.code); setShowLangMenu(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition-colors flex items-center gap-2 ${lang === l.code ? "text-[#00F0FF]" : "text-[#A0A0B0]"}`}>
                      <span className="font-['JetBrains_Mono'] uppercase w-5">{l.code}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <>
                {/* Role Toggle */}
                <button
                  data-testid="role-toggle-btn"
                  onClick={handleToggleRole}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300"
                  style={{
                    borderColor: role === "client" ? "rgba(0,240,255,0.4)" : "rgba(255,184,0,0.4)",
                    background: role === "client" ? "rgba(0,240,255,0.08)" : "rgba(255,184,0,0.08)",
                  }}
                >
                  {role === "client" ? (
                    <User className="w-4 h-4 text-[#00F0FF]" />
                  ) : (
                    <Briefcase className="w-4 h-4 text-[#FFB800]" />
                  )}
                  <span
                    className="text-sm font-medium font-['Outfit']"
                    style={{ color: role === "client" ? "#00F0FF" : "#FFB800" }}
                  >
                    {role === "client" ? t.roles.client : t.roles.freelancer}
                  </span>
                </button>

                {/* User menu */}
                <div className="flex items-center gap-2">
                  <Link to="/profile" data-testid="nav-profile-link" className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors" title={t.nav.profile}>
                    <UserCircle className="w-4 h-4 text-[#A0A0B0]" />
                  </Link>
                  <NotificationDropdown user={user} />
                  <span className="text-xs text-[#A0A0B0] font-['JetBrains_Mono'] hidden sm:inline">
                    {user.name || user.email}
                  </span>
                  <button
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    title={t.nav.logout}
                  >
                    <LogOut className="w-4 h-4 text-[#A0A0B0]" />
                  </button>
                </div>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login-btn">
                <button className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/8 text-[#00F0FF] text-sm font-['Outfit'] font-medium hover:bg-[#00F0FF]/15 transition-all">
                  <LogIn className="w-4 h-4" />
                  {t.nav.login}
                </button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
